import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Target, Shield, TrendingUp } from 'lucide-react';
export function CJIPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-163" data-mp="l822a71uxvgb">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-164" data-mp="78atckq77596">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-165" data-mp="b9ciu9ubc2xy">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-166" data-mp="q6n4gc0o6p7e">
            <span className="w-8 h-px bg-teal" data-id="element-167" data-mp="q40ts3ihftky"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-168" data-mp="u6495389vibi">
              Civic Judgment Index
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-169" data-mp="1hv6scfik1j84"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-170" data-mp="1mp1672bhb3za">
            The Analytical Core of Civic Judgment
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-171" data-mp="36jrqvbbmddyk">
            The Civic Judgment Index (CJI) is the foundational analytical layer
            of the SyNeural platform. It is designed to measure and strengthen
            reasoning quality, decision competence, manipulation resistance, and
            leadership judgment across civic, institutional, and
            high-responsibility environments.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-172" data-mp="80b4fvgqwics">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="prose prose-lg max-w-none" data-id="element-173" data-mp="32rq6vk1s5ku">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-174" data-mp="dp1lkmklhqgr">
            Modern societies rely on judgment everywhere, but rarely have a
            clear framework for evaluating it. People are expected to make
            decisions under uncertainty, sort signal from noise, resist
            emotional manipulation, and act with foresight in environments
            shaped by complexity, pressure, and competing narratives.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-175" data-mp="wmmb7vv6ot2f">
            CJI was created as the foundational analytical structure for
            understanding how people reason, how they respond to persuasion and
            pressure, and how decision quality can be strengthened over time.
          </p>
        </motion.div>
      </section>

      {/* Why CJI Exists */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-176" data-mp="1fdvidyhhf5qu">
        <div className="max-w-4xl mx-auto" data-id="element-177" data-mp="1piv8rnia88yn">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-178" data-mp="1pjn3yx7yw4oe">
            <div className="flex items-center gap-4 mb-6" data-id="element-179" data-mp="288jxgyvjit5k">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-180" data-mp="2881sndutb6m1">
                <Target size={24} data-id="element-181" data-mp="23btjch3aq7ms" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-182" data-mp="1yi8lqq5m7fo4">
                Why CJI Exists
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-183" data-mp="1ufc87duxm9r1">
              Judgment is one of the most important variables in modern society,
              yet institutions often evaluate credentials, outputs, or
              compliance without examining the underlying quality of reasoning
              that shapes decisions. Weak foresight, emotional reactivity, and
              manipulation vulnerability can create consequences far beyond a
              single moment. CJI exists to provide a more structured way to
              assess the core dimensions of judgment that matter in civic,
              professional, institutional, and leadership settings.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What CJI Measures */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-184" data-mp="2ueukpcc89k85">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-185" data-mp="2gpkde9mgnmbx">
          <div className="flex items-center gap-4 mb-6" data-id="element-186" data-mp="3d7oy41u7d4a9">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-187" data-mp="11rohg96ct6pm">
              <Brain size={24} data-id="element-188" data-mp="wsldks2416j8" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-189" data-mp="wrtmnujgjwza">
              What CJI Measures
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed mb-8" data-id="element-190" data-mp="1bjc5s17m67mv">
            CJI focuses on the key dimensions that shape civic judgment and
            practical decision-making. These include:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-191" data-mp="dtza56j4f3c5">
            {[{
            name: 'Reasoning quality',
            link: '/reasoning-quality'
          }, {
            name: 'Consequential thinking',
            link: '/consequential-thinking'
          }, {
            name: 'Manipulation resistance',
            link: '/manipulation-resistance'
          }, {
            name: 'Emotional regulation',
            link: '/emotional-regulation'
          }, {
            name: 'Social cooperation',
            link: '/social-cooperation'
          }, {
            name: 'Leadership responsibility',
            link: '/leadership-responsibility'
          }].map((item, index) => <motion.a key={item.name} href={item.link} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.1
          }} className="flex items-center gap-3 bg-white p-4 rounded-lg border border-line hover:border-navy hover:shadow-md transition-all group" data-id="element-192" data-mp="1njsjuncr9vri" data-mpi={index}>
                <div className="w-2 h-2 rounded-full bg-navy" data-id="element-193" data-mp="3n23zw1h3ld3t"></div>
                <span className="text-ink-dark font-medium group-hover:text-navy transition-colors" data-id="element-194" data-mp="3j0si2i4gtw66">
                  {item.name}
                </span>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-navy" data-id="element-195" data-mp="3e422rx0hj4lt">
                  <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-196" data-mp="2vbhgiyfajxqz" />
                </svg>
              </motion.a>)}
          </div>
          <p className="text-lg text-ink-muted leading-relaxed mt-8" data-id="element-197" data-mp="2gdp2ybtb1jff">
            Together, these domains help create a more rigorous picture of how
            judgment functions under complexity, pressure, and responsibility.
          </p>
        </motion.div>
      </section>

      {/* The Role of CJI */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-198" data-mp="3vb03xry4ksmv">
        <div className="max-w-4xl mx-auto" data-id="element-199" data-mp="43e121j8cu72">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-200" data-mp="422nj9zzg82r">
            <div className="flex items-center gap-4 mb-6" data-id="element-201" data-mp="3hik4f14fncqp">
              <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-202" data-mp="3hj1xjovstaue">
                <Shield size={24} data-id="element-203" data-mp="32nm9c0hwg95x" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-204" data-mp="37jkvdhfr9f36">
                The Role of CJI in the Platform
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-205" data-mp="2jx8wezcc466d">
              CJI serves as the analytical base layer of SyNeural. It is the
              platform's core engine for decision-quality analytics,
              propaganda-resistance profiling, cognitive pattern assessment, and
              leadership reasoning diagnostics. Where NCJI expands into broader
              developmental pathways, lifecycle architecture, and institutional
              deployment, CJI provides the essential measurement logic that
              makes those higher-order applications possible.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How CJI Creates Value */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-206" data-mp="3qlp7mo0sd7qu">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-207" data-mp="3qlxo0pw4av6s">
          <div className="flex items-center gap-4 mb-6" data-id="element-208" data-mp="j4e13gpu7mfz">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-209" data-mp="e85nw8v7a3bg">
              <TrendingUp size={24} data-id="element-210" data-mp="a73ptvd0rxb2" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-211" data-mp="16vjfdrg4q6o6">
              How CJI Creates Value
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-212" data-mp="11x86bixns070">
            CJI is designed to make judgment more visible, more structured, and
            more actionable. It helps individuals, institutions, and leadership
            environments better understand where reasoning is strong, where
            vulnerabilities exist, and where development is needed. By
            identifying patterns, clarifying risks, and supporting stronger
            decision-making, CJI creates a more useful analytical foundation for
            growth and readiness.
          </p>
        </motion.div>
      </section>

      {/* Use Cases */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-213" data-mp="1y0xihsb1u4ju">
        <div className="max-w-5xl mx-auto" data-id="element-214" data-mp="223272cmn4azm">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-215" data-mp="26vvex9denzfx">
            <h2 className="text-3xl font-heading font-bold text-ink-dark mb-12 text-center" data-id="element-216" data-mp="2lqrqnwkfb4xr">
              Use Cases
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-217" data-mp="rf4plsaxu88x">
              {[{
              title: 'Education',
              desc: 'Support reasoning, media literacy, and judgment development',
              link: '/education'
            }, {
              title: 'Employers & Organizations',
              desc: 'Strengthen decision quality, communication, and advancement readiness',
              link: '/employers'
            }, {
              title: 'Civil Society',
              desc: 'Support misinformation resilience and collective reasoning capacity',
              link: '/civil-society'
            }, {
              title: 'Government',
              desc: 'Structured framework for understanding readiness and judgment quality',
              link: '/government'
            }, {
              title: 'Leadership',
              desc: 'High-responsibility decision-making and executive evaluation',
              link: '/leadership'
            }].map((useCase, index) => <motion.a key={useCase.title} href={useCase.link} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-white p-6 rounded-lg border border-line hover:border-navy hover:shadow-md transition-all group" data-id="element-218" data-mp="3f2wqbkpz35y3" data-mpi={index}>
                  <h3 className="text-lg font-heading font-bold text-ink-dark mb-2 group-hover:text-navy transition-colors" data-id="element-219" data-mp="2rdtyxl5jdyqe">
                    {useCase.title}
                  </h3>
                  <p className="text-ink-muted text-sm leading-relaxed mb-3" data-id="element-220" data-mp="2dg5mn1pjbzwy">
                    {useCase.desc}
                  </p>
                  <div className="flex items-center gap-1 text-navy text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-221" data-mp="2aczinxlh5uvn">
                    Learn More
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-222" data-mp="20mn2ehkp0nvb">
                      <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-223" data-mp="1rn025z3p9o3u" />
                    </svg>
                  </div>
                </motion.a>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CJI and NCJI */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-224" data-mp="38089m848vw51">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-navy-deep text-white p-12 rounded-2xl" data-id="element-225" data-mp="37y3wr0odbmsm">
          <h2 className="text-3xl font-heading font-bold mb-6" data-id="element-226" data-mp="17ikexc5jemor">CJI and NCJI</h2>
          <p className="text-lg leading-relaxed opacity-90" data-id="element-227" data-mp="aumqaq01bfpw">
            Within SyNeural's broader ecosystem, CJI serves as the core
            analytical layer. NCJI builds on that foundation to create a wider
            developmental, institutional, and leadership-readiness system.
            Together, they form a unified architecture for understanding,
            strengthening, and scaling judgment across the full human and
            institutional spectrum.
          </p>
        </motion.div>
      </section>
    </div>;
}